import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { resolveLandingAssetUrl } from "../../../../utils/landingAssets";
import { getValueAtDescriptorPath } from "../elementEditing";
import ImageElementEditor from "./ImageElementEditor";
import TextElementEditor from "./TextElementEditor";

function collectionProperty(descriptor) {
  return descriptor?.property || descriptor?.collection?.property || null;
}

function fieldDefinition(property, key) {
  return (property?.item?.fields || []).find((field) => field?.key === key) || null;
}

function moveItem(items, from, to) {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function slideName(item, index) {
  const caption = String(item?.caption || "").trim();
  const alt = String(item?.imageAlt || "").trim();
  return caption || alt || `Imagen ${index + 1}`;
}

function buildImageDescriptor(descriptor, property, index) {
  const imageDefinition = fieldDefinition(property, "imageUrl");
  if (!imageDefinition) return null;
  const altDefinition = fieldDefinition(property, "imageAlt");
  const positionDefinition = fieldDefinition(property, "imagePosition");
  const imagePath = `${property.path}.${index}.imageUrl`;
  const altPath = `${property.path}.${index}.imageAlt`;
  const positionPath = `${property.path}.${index}.imagePosition`;
  return {
    ...descriptor,
    kind: "image",
    label: "Imagen del carrusel",
    fieldPath: imagePath,
    canvasPath: imagePath,
    field: {
      ...imageDefinition,
      path: imagePath,
      label: "Imagen",
      altPath,
      positionPath,
      positionOptions: positionDefinition?.options || [],
      positionLabel: "Encuadre",
    },
    altField: altDefinition
      ? { ...altDefinition, path: altPath, label: "Texto alternativo" }
      : null,
    collection: { property, index, itemKey: "imageUrl", itemField: imageDefinition },
    chips: [`Imagen ${index + 1}`],
    styleFields: imageDefinition.stylePrefix
      ? (descriptor?.definition?.properties || []).filter((candidate) => String(candidate?.path || "").startsWith(imageDefinition.stylePrefix))
      : [],
  };
}

function buildCaptionDescriptor(descriptor, property, index) {
  const definition = fieldDefinition(property, "caption");
  if (!definition) return null;
  const path = `${property.path}.${index}.caption`;
  return {
    ...descriptor,
    kind: "text",
    label: "Epígrafe",
    fieldPath: path,
    canvasPath: path,
    field: { ...definition, path, label: "Epígrafe" },
    colorPath: definition.colorPath || "imageCarousel.captionStyle.color",
    collection: { property, index, itemKey: "caption", itemField: definition },
    chips: [`Imagen ${index + 1}`],
    styleFields: definition.stylePrefix
      ? (descriptor?.definition?.properties || []).filter((candidate) => String(candidate?.path || "").startsWith(definition.stylePrefix))
      : [],
  };
}

function SlideThumb({ item, index, total, disabled, onOpen, onMoveUp, onMoveDown, onRemove }) {
  const imageUrl = resolveLandingAssetUrl(item?.imageUrl);
  const name = slideName(item, index);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.8,
        borderRadius: 2.5,
        borderColor: "rgba(25,36,47,.12)",
        bgcolor: "#fff",
      }}
    >
      <Button
        fullWidth
        disabled={disabled}
        onClick={onOpen}
        aria-label={`Editar ${name}`}
        sx={{
          minWidth: 0,
          p: 0.6,
          display: "grid",
          gridTemplateColumns: "78px minmax(0, 1fr)",
          gap: 1,
          alignItems: "center",
          color: "inherit",
          textAlign: "left",
          textTransform: "none",
          borderRadius: 1.8,
        }}
      >
        <Box
          sx={{
            width: 78,
            height: 58,
            overflow: "hidden",
            borderRadius: 1.5,
            bgcolor: "rgba(25,36,47,.05)",
            display: "grid",
            placeItems: "center",
          }}
        >
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <AddPhotoAlternateRoundedIcon sx={{ color: "rgba(25,36,47,.38)" }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.55)", fontWeight: 850 }}>
            Imagen {index + 1}
          </Typography>
          <Typography noWrap sx={{ mt: 0.15, fontSize: ".88rem", fontWeight: 950, color: "#19242F" }} title={name}>
            {name}
          </Typography>
          <Typography variant="caption" noWrap sx={{ display: "block", mt: 0.15, color: "rgba(25,36,47,.56)" }}>
            {imageUrl ? "Imagen cargada" : "Sin imagen"}
          </Typography>
        </Box>
      </Button>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.35 }}>
        <Stack direction="row" spacing={0.15}>
          <Tooltip title="Mover antes">
            <span>
              <IconButton size="small" disabled={disabled || index === 0} onClick={onMoveUp} aria-label={`Mover imagen ${index + 1} antes`}>
                <KeyboardArrowUpRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mover después">
            <span>
              <IconButton size="small" disabled={disabled || index >= total - 1} onClick={onMoveDown} aria-label={`Mover imagen ${index + 1} después`}>
                <KeyboardArrowDownRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Tooltip title="Eliminar imagen">
          <span>
            <IconButton size="small" color="error" disabled={disabled} onClick={onRemove} aria-label={`Eliminar imagen ${index + 1}`}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

export default function ImageCarouselElementEditor({
  descriptor,
  form,
  baselineForm,
  disabled,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const property = collectionProperty(descriptor);
  const collectionPath = property?.path || "";
  const rawItems = collectionPath ? getValueAtDescriptorPath(form, collectionPath, []) : [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const maxItems = Number.isFinite(Number(property?.limits?.maxItems)) ? Number(property.limits.maxItems) : 20;
  const directIndex = descriptor?.kind === "collection-item" && Number.isInteger(descriptor?.collection?.index)
    ? descriptor.collection.index
    : null;
  const [selectedIndex, setSelectedIndex] = React.useState(directIndex);

  React.useEffect(() => {
    setSelectedIndex(directIndex);
  }, [collectionPath, directIndex]);

  React.useEffect(() => {
    if (!Number.isInteger(selectedIndex)) return;
    if (selectedIndex >= items.length) setSelectedIndex(items.length ? items.length - 1 : null);
  }, [items.length, selectedIndex]);

  if (!property) return null;

  const commitItems = (next) => onCommit?.(collectionPath, next.slice(0, maxItems));
  const addItem = () => {
    if (disabled || items.length >= maxItems) return;
    const next = [
      ...items,
      { imageUrl: "", imageAlt: "", caption: "", imagePosition: "center" },
    ];
    commitItems(next);
    setSelectedIndex(next.length - 1);
  };
  const removeItem = (index) => {
    if (disabled || index < 0 || index >= items.length) return;
    commitItems(items.filter((_, itemIndex) => itemIndex !== index));
    if (selectedIndex === index) setSelectedIndex(null);
  };
  const reorder = (from, to) => {
    if (disabled || to < 0 || to >= items.length) return;
    commitItems(moveItem(items, from, to));
    if (selectedIndex === from) setSelectedIndex(to);
    else if (selectedIndex === to) setSelectedIndex(from);
  };

  if (Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < items.length) {
    const item = items[selectedIndex] || {};
    const imageDescriptor = buildImageDescriptor(descriptor, property, selectedIndex);
    const captionDescriptor = buildCaptionDescriptor(descriptor, property, selectedIndex);

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Button
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => setSelectedIndex(null)}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Volver a imágenes
          </Button>
          <Chip size="small" label={`${selectedIndex + 1} / ${items.length}`} variant="outlined" />
        </Stack>

        <Box>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{slideName(item, selectedIndex)}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>
            Editás únicamente esta imagen. El orden se conserva al guardar y publicar.
          </Typography>
        </Box>

        {imageDescriptor ? (
          <ImageElementEditor
            descriptor={imageDescriptor}
            form={form}
            baselineForm={baselineForm}
            disabled={disabled}
            onCommit={onCommit}
            onUploadImage={onUploadImage}
            onRemoveImage={onRemoveImage}
          />
        ) : null}

        {captionDescriptor ? (
          <TextElementEditor
            descriptor={captionDescriptor}
            form={form}
            baselineForm={baselineForm}
            disabled={disabled}
            onCommit={onCommit}
          />
        ) : null}

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || selectedIndex === 0}
            startIcon={<KeyboardArrowUpRoundedIcon />}
            onClick={() => reorder(selectedIndex, selectedIndex - 1)}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Mover antes
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || selectedIndex >= items.length - 1}
            startIcon={<KeyboardArrowDownRoundedIcon />}
            onClick={() => reorder(selectedIndex, selectedIndex + 1)}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Mover después
          </Button>
          <Button
            size="small"
            color="error"
            disabled={disabled}
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => removeItem(selectedIndex)}
            sx={{ textTransform: "none", fontWeight: 850, ml: { sm: "auto" } }}
          >
            Eliminar
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Imágenes del carrusel</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>
            Tocá una imagen para editarla. Usá las flechas de la vista previa para recorrer el carrusel.
          </Typography>
        </Box>
        <Chip size="small" label={`${items.length} / ${maxItems}`} color={items.length >= maxItems ? "warning" : "default"} />
      </Stack>

      {items.length ? (
        <Stack spacing={0.8}>
          {items.map((item, index) => (
            <SlideThumb
              key={`${collectionPath}:${index}:${item?.imageUrl || item?.caption || "empty"}`}
              item={item || {}}
              index={index}
              total={items.length}
              disabled={disabled}
              onOpen={() => setSelectedIndex(index)}
              onMoveUp={() => reorder(index, index - 1)}
              onMoveDown={() => reorder(index, index + 1)}
              onRemove={() => removeItem(index)}
            />
          ))}
        </Stack>
      ) : (
        <Box sx={{ p: 2.2, textAlign: "center", border: "1px dashed rgba(25,36,47,.18)", borderRadius: 2.5, bgcolor: "rgba(25,36,47,.02)" }}>
          <AddPhotoAlternateRoundedIcon sx={{ mb: 0.6, color: "rgba(25,36,47,.35)" }} />
          <Typography variant="body2" sx={{ fontWeight: 900, color: "rgba(25,36,47,.72)" }}>Todavía no agregaste imágenes.</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>Agregá la primera y después podés ordenar el resto visualmente.</Typography>
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<AddPhotoAlternateRoundedIcon />}
        disabled={disabled || items.length >= maxItems}
        onClick={addItem}
        sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}
      >
        Agregar imagen
      </Button>
    </Stack>
  );
}
